import FilledButtonM from "@common/components/button/FilledButtonM"
import { FormStepper } from "@common/components/stepper/FormStepper"
import TextM from "@common/components/text/TextM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { congratsTypes } from "@common/screens/CongratsScreen"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { putCongrats } from "@data/redux/actions/navigation"
import { tutorsService } from "@data/services/tutorsService"
import { hasBothSubjectTypes } from "@data/utility/dataUtils"
import { validation } from "@data/utility/validation"
import moment from "moment"
import React, { useContext, useEffect, useState } from "react"
import { View } from "react-native"
import { StatusBar } from "@common/systemBars/StatusBar"
import { ActivityIndicator } from "react-native-paper"
import { connect } from "react-redux"
import PrecisionAnswerComponent from "../PrecisionAnswerComponent"
import InterviewQuestion from "./InterviewQuestion"

export const getCongratsText = (step, user = null) => {
    const relevantSubjects = user?.profile?.relevant_subjects ?? []
    const hasBothTypes = hasBothSubjectTypes(relevantSubjects)

    switch (step) {
        case 2:
            return "Bravo tu as fini cette étape ! Tu va maintenant pouvoir passer à montrer ta motivation en vidéo !"

        case 3:
            return "Super tu as fini cette étape ! Tu vas pouvoir passer à l'entretien de Zoé."

        case 4:
            return "Merci pour tes réponses ! Tu vas maintenant pouvoir passer à l'entretien en vidéo !"

        case 5:
            return "Génial tu as fini cette étape ! Tu vas pouvoir passer à l'entretien de Sam."

        case 6:
            // If tutor has both types, step 6 is literary (not final), step 7 is scientific (final)
            if (hasBothTypes) {
                return "Génial tu as fini cette question ! Tu vas pouvoir passer à la prochaine question !"
            }
            // If tutor has one type, step 6 is the final technical step
            return "C'était le dernier passage de ton dépôt de candidature. Nous reviendrons vers toi dans les plus brefs délais !"

        case 7:
            // Step 7 is only for tutors with both types, and it's the final step
            return "C'était le dernier passage de ton dépôt de candidature. Nous reviendrons vers toi dans les plus brefs délais !"

        default:
    }
}

const InterviewScreen = (props) => {
    const { navigation } = props
    const [formValues, setFormValues] = useState(null)
    const testCategory =
        props.route.params?.testCategory?.split("_")[0] ?? "motivation"
    const isVideoStep =
        props.route.params?.testCategory?.split("_")[1] === "video"
    const stepNum = Number(props.route.params?.stepNum)

    const defaultValues = {}
    const currentStepName = testCategory
    const [champion, setChampion] = useState(
        testCategory === "motivation"
            ? "tina"
            : testCategory === "educational"
            ? "zoe"
            : null
    )
    const [precisions, setPrecisions] = useState({})

    const questions =
        props.testQuestions?.[0]?.category === currentStepName
            ? props.testQuestions
            : []

    useEffect(() => {
        if (isVideoStep) {
            const currentStep = props.user?.application_steps?.[stepNum - 1]
            const subjectType = currentStep?.subject_type

            const queryParams = {
                test_category: currentStepName,
                step_type: "video",
            }

            if (subjectType) {
                queryParams.subject_type = subjectType
            }

            props.apiRequest(
                tutorsService.getTestQuestions,
                {},
                {},
                queryParams
            )
        }
    }, [])

    useApiStatus(
        tutorsService.getTestQuestions, // api service
        null, // success message on toast (string or null)
        false, // error message from server in toast (true) or keep it in redux state (false)
        () => {
            setChampion(props.testQuestions?.[0]?.champion)
        }
    )

    const motivationQuestions = [
        {
            label: "As-tu déjà donné des cours de soutien ?",
            type: "select.single", // select.single, select.multiple, text, date
            name: "mot_support",
            options: [
                { label: "Oui", value: "Oui" },
                { label: "Non", value: "Non" },
            ],
            defaultValue: null,
            validationRules: { ...validation.required() },
            showNextButton: false,
        },
        {
            label: "Dans quel cadre ?",
            type: "select.multiple", // select.single, select.multiple, text, date
            name: "mot_scope",
            options: [
                {
                    label: "Cours en présentiel avec une agence",
                    value: "Cours en présentiel avec une agence",
                },
                {
                    label: "Cours en distanciel avec une agence",
                    value: "Cours en distanciel avec une agence",
                },
                {
                    label: "Au sein de ma famille",
                    value: "Au sein de ma famille",
                },
                { label: "Autre", value: "autre" },
            ],
            defaultValue: null,
            validationRules: { ...validation.required() },
            showNextButton: false,
            is_masked: formValues?.mot_support !== "Oui",
        },
        {
            label: "Pendant combien de temps ?",
            type: "select.single", // select.single, select.multiple, text, date
            name: "mot_duration",
            options: [
                { label: "Moins de 6 mois", value: "moins_6_mois" },
                { label: "Moins d'1 an", value: "moins_1_an" },
                { label: "Plus d'1 an", value: "plus_1_an" },
            ],
            defaultValue: null,
            validationRules: { ...validation.required() },
            showNextButton: false,
            is_masked: formValues?.mot_support !== "Oui",
        },
        {
            label: "Pour quelles classes ?",
            type: "select.multiple", // select.single, select.multiple, text, date
            name: "mot_classes",
            options: [
                {
                    label: "Essentiellement primaire",
                    value: "Essentiellement primaire",
                },
                {
                    label: "Essentiellement collégien",
                    value: "Essentiellement collégien",
                },
                {
                    label: "Essentiellement lycéen",
                    value: "Essentiellement lycéen",
                },
                {
                    label: "Essentiellement post bac",
                    value: "Essentiellement post bac",
                },
            ],
            defaultValue: null,
            validationRules: { ...validation.required() },
            showNextButton: false,
            is_masked: formValues?.mot_support !== "Oui",
        },
        {
            label: "Indique moi le nombre d'heures de cours que tu es prêt à donner chaque semaine de manière régulière ?",
            type: "select.single", // select.single, select.multiple, text, date
            name: "mot_tutoring_hrs",
            options: [
                {
                    label: "Entre 2h et 4h par semaine",
                    extraLabel:
                        "(rémunération espérée : entre 160 et 300 €/mois)",
                    value: "2h à 4h",
                },
                {
                    label: "Entre 4h et 6h par semaine",
                    extraLabel:
                        "(rémunération espérée : entre 300 et 450 €/mois)",
                    value: "4h à 6h",
                },
                {
                    label: "Entre 6h et 8h par semaine",
                    extraLabel:
                        "(rémunération espérée : entre 450 et 600 €/mois)",
                    value: "6h à 8h",
                },
                {
                    label: "Plus de 8h par semaine",
                    extraLabel: "(rémunération espérée : 600 €/mois et plus)",
                    value: "Plus de 8h",
                },
            ],
            defaultValue: null,
            validationRules: { ...validation.required() },
            showNextButton: false,
        },
    ]

    // Helper function to create competency options with disabled logic
    const createCompetencyOptions = (
        competencies,
        selectedCompetencies,
        maxSelection = 2
    ) => {
        return competencies.map((competency) => ({
            label: competency,
            value: competency,
            disabled:
                selectedCompetencies?.length >= maxSelection &&
                !selectedCompetencies?.includes(competency),
        }))
    }

    const competencyOptions = [
        "Expliquer clairement des notions de mon domaine",
        "Redonner confiance à un élève",
        "Nouer un lien fort avec un élève",
        "Remotiver un élève",
        "Aider un élève à s'organiser",
        "Faire preuve de patience avec un élève en grande difficulté",
    ]

    const pedagogyQuestions = [
        {
            label: "As tu une préférence pour accompagner des jeunes élèves ou des élèves plus avancés ?",
            type: "custom",
            name: "edu_grade_preference",
            component: PrecisionAnswerComponent,
            componentProps: {
                setPrecisions,
                precisions,
                inputName: "Pourquoi ?",
                data: [
                    {
                        label: "Je préfère accompagner des élèves du CM1 à la 5ème",
                        value: "Je préfère accompagner des élèves du CM1 à la 5ème",
                    },
                    {
                        label: "Je préfère accompagner des élèves de la 4ème à la 2nde",
                        value: "Je préfère accompagner des élèves de la 4ème à la 2nde",
                    },
                    {
                        label: "Je préfère accompagner des élèves de la 1ère et plus",
                        value: "Je préfère accompagner des élèves de la 1ère et plus",
                    },
                    {
                        label: "Je n'ai pas de préférence",
                        value: "Je n'ai pas de préférence",
                    },
                ],
                optionalPrecision: true,
                placeHolderText:
                    "Si tu le souhaites, explique moi ton choix, ça m'aidera à mieux te connaître.",
            },
            defaultValue: null,
            validationRules: { ...validation.required() },
            showNextButton: false,
        },
        {
            label: "Parmi les compétences pédagogiques suivantes, peux-tu m’en citer 2 que tu penses maîtriser ?",
            type: "select.multiple",
            name: "edu_competencies",
            options: createCompetencyOptions(
                competencyOptions,
                formValues?.edu_competencies,
                2
            ),
            defaultValue: [],
            validationRules: {
                ...validation.required(),
                ...validation.minLength(2),
            },
            showNextButton: false,
        },
    ]

    const callBackOnSubmit = () => {
        const congratsText = getCongratsText(stepNum, props.user)

        const congrats = [
            {
                congratsType: congratsTypes.APPLICATION_STEP_CONGRATS,
                congratsData: {
                    champion: champion,
                    text: congratsText,
                },
            },
        ]

        props.putCongrats(congrats)
        navigation.replace("congrats")
    }

    const formData =
        questions
            ?.filter((q) => q.answer_type === "video")
            ?.map((q) => {
                const answerOptions = q?.answer_options?.map((option) => {
                    return { label: option, value: option }
                })

                const answer = props.testAnswers?.find(
                    (answer) =>
                        answer?.application_question_id === parseInt(q?.id)
                )
                const tooLate =
                    answer?.viewed_at &&
                    moment().diff(answer?.viewed_at, "seconds") > 10 * 60
                const canFinish =
                    !!answer?.video ||
                    answer?.answer?.startsWith("video_tmp:") ||
                    tooLate

                const questionName = q?.activity?.student_class
                    ? `${q?.question} (copie d'un élève en ${q?.activity.student_class})`
                    : q?.question

                return {
                    label: questionName,
                    type: "custom",
                    name: q?.category + "_" + q?.id,
                    options: answerOptions ?? [],
                    defaultValue: null,
                    validationRules: { ...validation.required() },
                    component: InterviewQuestion,
                    componentProps: {
                        canFinish,
                        tooLate,
                        callBackOnSubmit,
                    },
                    showNextButton: false,
                    is_masked:
                        q?.viewed_at ||
                        (q?.answer_type !== "video" &&
                            formValues?.[q?.category + "_" + 1] === "Non"),
                    removeExitButton: canFinish,
                }
            }) ?? []

    const formQuestions = isVideoStep
        ? formData
        : testCategory === "motivation"
        ? motivationQuestions
        : testCategory === "educational"
        ? pedagogyQuestions
        : []

    const onSubmit = (data) => {
        const transformedData = Object.keys(data).map((key) => {
            const value = data[key]

            const answerObject = {
                question_slug: key,
                answer:
                    key === "mot_rem_percentage"
                        ? {
                              mot_rem_percentage: value,
                              mot_trans_percentage:
                                  data["mot_trans_percentage"],
                          }
                        : Array.isArray(value)
                        ? value
                        : { text: value },
            }

            if (precisions[key]) {
                answerObject.answer.precision = precisions[key]
            }

            return answerObject
        })

        props.apiRequest(
            tutorsService.submitTestAnswers,
            {},
            {
                application_question_answers: transformedData.filter((x) => x),
            }
        )
    }

    useApiStatus(
        tutorsService.submitTestAnswers, // api service
        null, // success message on toast (string or null)
        false, // error message on toast (true) or keep it in redux state (false)
        () => {
            callBackOnSubmit()
        }
    )

    const dimensions = useContext(DimensionsContext)
    return (
        <View
            style={{
                flex: 1,
                alignItems: "center",
                justifyContent: "center",
                width: dimensions.width,
                alignSelf: "center",
            }}
        >
            {props.getTestQuestionsRequest ? (
                <ActivityIndicator color={theme.colors.primary} />
            ) : formQuestions?.length === 0 ? (
                <>
                    <TextM
                        wrap
                        style={{ textAlign: "center", marginBottom: 10 }}
                    >
                        Tu as déjà vu cette question, passe à la suite.
                    </TextM>
                    <FilledButtonM
                        onPress={() => {
                            navigation.goBack()
                        }}
                        label={"Continuer"}
                    />
                </>
            ) : (
                <FormStepper
                    navigation={navigation}
                    track
                    formData={formQuestions?.filter((x) => !x.is_masked)}
                    champion={{
                        name: champion,
                        variant: "main",
                    }}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    defaultValues={defaultValues}
                    onSubmit={onSubmit}
                    submitted={props.submitTestAnswersRequest}
                    previousRouteName={"menu"}
                    submittedMessage=""
                    serverError={props.submitTestAnswersError}
                    exitWarning
                    exitWarningMessage={
                        "Si tu quittes cet écran avant d'avoir soumis ta réponse, c'est comme si tu avais passé la question."
                    }
                    formId="interview-step"
                    entryId={props.user?.id}
                />
            )}
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        getTestQuestionsSuccess: state.api.getTestQuestions?.success,
        getTestQuestionsRequest: state.api.getTestQuestions?.request,
        getTestQuestionsError: state.api.getTestQuestions?.error,
        user: state.data.currentUser?.object,
        testQuestions: state.data.tutors?.testQuestions,
        testAnswers: state.data.tutors?.testAnswers,
        schoolSubjects: state.data.staticData?.school_subjects,
        submitTestAnswersRequest: state.api.submitTestAnswers?.request,
        submitTestAnswersError: state.api.submitTestAnswers?.error,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    putCongrats,
}

export default connect(mapStateToProps, mapDispatchToProps)(InterviewScreen)
